import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { OrganizationSwitcher, UserButton } from '@clerk/clerk-react';
import { LuBuilding, LuChevronDown, LuPenLine } from 'react-icons/lu';
import { TbDatabaseEdit } from 'react-icons/tb';

import { Department } from '@/entities/department';
import OrganizationSettingsPage from '@/pages/organization-settings';
import headerLogo from '@/shared/assets/images/logo-header.png';

// ★ 追加: CSS Modules をインポート
import styles from './styles.module.css';

// Props の型定義 (AppLayout から渡される想定)
interface HeaderProps {
  userDepartments: Department[];
  selectedDeptId: string | null;
  isDeptLoading: boolean; // isLoading の一部として渡すか、個別で渡すか
  onDeptChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  // isGenActive, isHistoryActive は Header 内部で計算可能
}

export const Header: React.FC<HeaderProps> = ({
  userDepartments,
  selectedDeptId,
  isDeptLoading,
  onDeptChange,
}) => {
  const location = useLocation();
  const isGenActive =
    location.pathname === '/' || location.pathname === '/result';
  const isHistoryActive = location.pathname === '/history';

  return (
    // ★ 修正: styles オブジェクトを使用
    <header className={styles.headerContainer}>
      <div className={styles.headerLeft}>
        <Link to="/">
          <img src={headerLogo} alt="ロゴ名" className={styles.headerLogo} />
        </Link>
        <div className={styles.leftControlsStack}>
          <OrganizationSwitcher
            appearance={{
              elements: {
                // ★ 必要であれば OrganizationSwitcher 用のクラス名を追加
                organizationSwitcherTrigger: styles.orgSwitcherTrigger,
              },
            }}
          >
            <OrganizationSwitcher.OrganizationProfilePage
              label="組織設定"
              url="settings"
              labelIcon={<LuBuilding />}
            >
              <OrganizationSettingsPage />
            </OrganizationSwitcher.OrganizationProfilePage>
          </OrganizationSwitcher>
          {userDepartments.length > 0 && (
            <div className={styles.departmentSelectorContainer}>
              <select
                className={styles.departmentSelect}
                value={selectedDeptId || ''}
                onChange={onDeptChange} // Props で受け取った関数を使用
                aria-label="Select Department"
                disabled={isDeptLoading} // isDeptLoading で無効化
              >
                {userDepartments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
              <div className={styles.dropdownArrowCircle}>
                <LuChevronDown className={styles.departmentSelectIcon} />
              </div>
            </div>
          )}
          {userDepartments.length === 0 && !isDeptLoading && (
            <span className={styles.noDepartmentMessage}>
              利用可能な部署がありません
            </span>
          )}
        </div>
      </div>
      <div className={styles.headerRight}>
        <div className={styles.desktopLinks}>
          <Link
            to="/"
            // ★ 修正: styles オブジェクトを使用し、条件付きクラス名を適用
            className={`${styles.headerLink} ${isGenActive ? styles.headerLinkActive : ''}`}
          >
            <LuPenLine
              className={`${styles.headerIcon} ${isGenActive ? styles.headerIconActive : ''}`}
            />
            文書生成
          </Link>
          <Link
            to="/history"
            className={`${styles.headerLink} ${isHistoryActive ? styles.headerLinkActive : ''}`}
          >
            <TbDatabaseEdit
              className={`${styles.headerIcon} ${isHistoryActive ? styles.headerIconActive : ''}`}
            />
            作成履歴
          </Link>
        </div>
        <div className={styles.authControls}>
          <UserButton />
        </div>
      </div>
    </header>
  );
};
